/**
 * The search fold, shared (PUX UI-2, PREP §3): how the client decides WHETHER
 * to ask the server, and how it compares a term with text already on screen.
 *
 * Copied from Quick Find (`features/search/term.ts` and `goto.ts`), which keeps
 * its own copies until pux-search points it here; until then the two are held
 * identical by test (`tests/pux-combo-search-fold.test.ts`).
 *
 * The server stays the only authority on matching for a record search
 * (SearchTerm.php: NFKC, Arabic letter and digit folding). These functions only
 * mirror that fold for text the client already holds: a small vocabulary
 * filtered locally, a highlight, an exact-code check.
 */

export const MIN_TERM = 2;
export const MAX_TERM = 100;
/** How long typing must pause before an option search asks the server. */
export const DEBOUNCE_MS = 250;

// Zero-width and bidi format controls (ZWSP…RLM, LRE…RLO, LRI…PDI, BOM).
const CONTROLS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;
// 0–9, Arabic-Indic and extended Arabic-Indic digits.
const DIGITS = /^[0-9\u0660-\u0669\u06F0-\u06F9]+$/;

/** A term without zero-width and bidi controls, outer and repeated whitespace. */
export function normaliseTerm(raw: string): string {
  return raw.replace(CONTROLS, '').replace(/\s+/gu, ' ').trim();
}

/** Whether a normalised term is worth a request: two characters, or one digit; at most 100. */
export function isSearchable(term: string): boolean {
  if (term.length > MAX_TERM) return false;
  if (term.length >= MIN_TERM) return true;

  return term.length === 1 && DIGITS.test(term);
}

// Harakat and tatweel (the server's fold, SearchTerm.php); the marks first, so no
// mark follows a letter inside the class (no-misleading-character-class).
const MARKS = /[\u064B-\u065F\u0670\u0640]/gu;
// أ إ آ ٱ → ا, ى → ي, ة → ه: each letter of FROM becomes the one at its place in TO.
const FROM = 'أإآٱىة';
const TO = 'اااايه';
const VARIANTS = /[أإآٱىة]/gu;
const ARABIC_INDIC = /[\u0660-\u0669]/gu;
const EXTENDED_INDIC = /[\u06F0-\u06F9]/gu;

function latinDigits(text: string): string {
  return text
    .replace(ARABIC_INDIC, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(EXTENDED_INDIC, (digit) => String(digit.charCodeAt(0) - 0x06f0));
}

/** The letter folds, on text already NFKC'd and normalised. */
function foldLetters(text: string): string {
  return latinDigits(
    text.replace(MARKS, '').replace(VARIANTS, (letter) => TO.charAt(FROM.indexOf(letter))),
  ).toLowerCase();
}

/**
 * A label or a term folded as the record search folds a term (SearchTerm.php):
 * NFKC, invisible controls and extra spaces gone, tatweel and harakat removed,
 * أ إ آ ٱ → ا, ى → ي, ة → ه, Arabic-Indic digits → 0–9; and lower case.
 * (Quick Find's `foldLabel`.)
 */
export function foldText(text: string): string {
  return foldLetters(normaliseTerm(text.normalize('NFKC')));
}

/**
 * A code (PO-2026-0042, RET-SUP-001) folded for an exact comparison: NFKC,
 * invisible controls and extra spaces gone, Arabic-Indic digits → 0–9, lower
 * case. Letters are not folded: a code is compared as written.
 */
export function foldCode(text: string): string {
  return latinDigits(normaliseTerm(text.normalize('NFKC'))).toLowerCase();
}

/** Whether `text` contains `term`, both folded: a local filter's substring match. */
export function includesFolded(text: string | null | undefined, term: string): boolean {
  if (!text) return false;

  return foldText(text).includes(foldText(term));
}

/** Folded text, and where each of its UTF-16 units came from in the original. */
export interface FoldedText {
  folded: string;
  /** For each unit of `folded`, the index in the original where its source begins. */
  start: number[];
  /** For each unit of `folded`, the index in the original just after its source. */
  end: number[];
}

// A base character with the combining marks that follow it, or marks with no base.
const CLUSTER = /\P{M}\p{M}*|\p{M}+/gu;
const SPACE = /\s/u;

/**
 * `foldText`, with an index map back into the original — so a match found in
 * the folded text can be marked in the text as written (a highlight).
 *
 * Folded one cluster (a base and its marks) at a time, so NFKC never composes
 * across the map; harakat, tatweel and controls fold to nothing and take no
 * index; an NFKC expansion maps every unit it produced to its one source.
 * `folded` equals `foldText(text)` (asserted by test).
 */
export function foldWithIndexMap(text: string): FoldedText {
  let folded = '';
  const start: number[] = [];
  const end: number[] = [];
  // As `normaliseTerm`: a run of whitespace becomes one space, and the outer
  // runs go — decided BEFORE harakat and tatweel are removed, as `foldText` does.
  let started = false;
  let gap: { from: number; to: number } | null = null;

  const push = (units: string, from: number, to: number) => {
    folded += units;
    for (let index = 0; index < units.length; index += 1) {
      start.push(from);
      end.push(to);
    }
  };

  for (const match of text.matchAll(CLUSTER)) {
    const from = match.index;
    const to = from + match[0].length;

    for (const unit of match[0].normalize('NFKC').replace(CONTROLS, '')) {
      if (SPACE.test(unit)) {
        if (started) gap = gap ? { from: gap.from, to } : { from, to };
        continue;
      }

      started = true;

      if (gap) {
        push(' ', gap.from, gap.to);
        gap = null;
      }

      push(foldLetters(unit), from, to);
    }
  }

  return { folded, start, end };
}

/**
 * The range of the original text that a range of the folded text came from:
 * `[from, to)` in `folded` → `[start, end)` in the original.
 */
export function originalRange(map: FoldedText, from: number, to: number): [number, number] {
  if (to <= from || from < 0 || to > map.folded.length) return [0, 0];

  return [map.start[from], map.end[to - 1]];
}
